import { type ReactElement } from "react";
import { Link } from "@inertiajs/react";
import { cn } from "@/lib/utils";

interface PartnerBrandProps {
    /** The tenant's name — the wordmark (D6/D7: the tenant is the brand). */
    name: string;
    /** The tenant's logo; without one a gold mark with the initial stands in. */
    logoUrl: string | null;
    /** Wrap the brand in a link (the store's home) when given. */
    href?: string;
    /** `panel` is the sign-in brand panel's dark surface; the wordmark turns light. */
    tone?: "default" | "panel";
    className?: string;
}

/**
 * Brand mark + serif wordmark, as the portal header and the sign-in panel
 * show it. The mark is the tenant logo when one is uploaded, else a rounded
 * gold square with the tenant's initial — never a hard-coded product name.
 */
export function PartnerBrand({ name, logoUrl, href, tone = "default", className }: PartnerBrandProps): ReactElement {
    const initial = name.trim().charAt(0) || "·";

    const content = (
        <>
            {logoUrl !== null ? (
                <img src={logoUrl} alt={name} className="h-7 w-auto max-w-[140px] object-contain" />
            ) : (
                <span
                    aria-hidden
                    className="flex h-7 w-7 items-center justify-center rounded-[7px] bg-primary font-serif text-lg italic leading-none text-primary-foreground"
                >
                    {initial.toLowerCase()}
                </span>
            )}
            <span
                className={cn(
                    "font-serif text-xl tracking-[-0.015em]",
                    tone === "panel" ? "text-white" : "text-foreground",
                    logoUrl !== null && "sr-only",
                )}
            >
                {name}
            </span>
        </>
    );

    const classes = cn("inline-flex items-center gap-3", className);

    if (href !== undefined) {
        return (
            <Link href={href} className={classes} aria-label={name}>
                {content}
            </Link>
        );
    }

    return <div className={classes}>{content}</div>;
}
